import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Search,
  ExpandMore,
  ExpandLess,
  Receipt,
  PhotoCamera,
  Notes,
  Payment,
  Person,
  Business,
  CalendarToday,
  LocationOn,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface ServiceSearchResult {
  serviceRequest: {
    _id: string;
    serviceRequestId: string;
    customer: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
    } | null;
    provider: {
      _id: string;
      firstName: string;
      lastName: string;
      businessName?: string;
      email: string;
      phone?: string;
    } | null;
    serviceType: {
      id: string;
      name: string;
      category: string;
    };
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
    };
    frequency: string;
    status: string;
    scheduledDate?: string;
    completedDate?: string;
    requestedDate: string;
    specialInstructions?: string;
    completionNotes?: string;
    estimatedDuration?: number;
    isImportedCustomer: boolean;
    createdAt: string;
    updatedAt: string;
  };
  jobCompletion: {
    _id: string;
    serviceRequestId: string;
    checkInTime?: string;
    checkOutTime?: string;
    completedAt?: string;
    notes?: string;
    issues?: string[];
    beforePhotos?: Array<{ url: string; createdAt: string; jobId: string }>;
    afterPhotos?: Array<{ url: string; createdAt: string; jobId: string }>;
    crewSize?: number;
    actualDuration?: number;
    paymentProcessed?: boolean;
    stripePaymentId?: string;
  } | null;
  payment: {
    _id: string;
    serviceRequestId: string;
    amount: number;
    providerCut: number;
    platformCut: number;
    stripePaymentIntentId?: string;
    stripeChargeId?: string;
    stripeCustomerId?: string;
    status: string;
    paymentMethod: string;
    completedAt?: string;
    serviceDate?: string;
    stripeSettlementStatus?: string;
    stripeSettledDate?: string;
    stripeBatchNumber?: string;
    createdAt: string;
  } | null;
  bid: {
    _id: string;
    servicePricing?: any;
    totalPrice?: number;
  } | null;
}

const AdminServiceSearchPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Search filters - simplified to essential fields only
  const [filters, setFilters] = useState({
    customerFirstName: '',
    customerLastName: '',
    providerName: '',
    providerEmail: '',
    serviceType: '',
    completedStartDate: '',
    completedEndDate: '',
  });

  // Results
  const [services, setServices] = useState<ServiceSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean;
    service: ServiceSearchResult | null;
  }>({ open: false, service: null });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  useEffect(() => {
    if (user?.userType !== 'admin') {
      navigate('/admin');
    }
  }, [user, navigate]);

  const handleSearch = async (pageNum: number = 1) => {
    setLoading(true);
    setError('');

    try {
      // Build query params - only essential filters
      const params: any = {
        page: pageNum,
        limit: pagination.limit,
      };

      if (filters.customerFirstName) params.customerFirstName = filters.customerFirstName;
      if (filters.customerLastName) params.customerLastName = filters.customerLastName;
      if (filters.providerName) params.providerName = filters.providerName;
      if (filters.providerEmail) params.providerEmail = filters.providerEmail;
      if (filters.serviceType) params.serviceType = filters.serviceType;
      if (filters.completedStartDate) params.completedStartDate = filters.completedStartDate;
      if (filters.completedEndDate) params.completedEndDate = filters.completedEndDate;

      console.log('[ADMIN-SEARCH-FRONTEND] Sending params:', params);
      const response = await apiService.get('/admin/services/search', { params }) as any;

      if (response.success && response.data) {
        setServices(response.data.services || []);
        setPagination(response.data.pagination || {
          page: 1,
          limit: 50,
          total: 0,
          pages: 0,
        });
      } else {
        setError(response.error || 'Failed to search services');
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to search services');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const toggleRowExpansion = (serviceId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return 'N/A';
    return `$${amount.toFixed(2)}`;
  };

  // Normalize service type names to standard format
  // Maps all mowing variations to "Lawn Mowing/Trimming"
  const formatServiceTypeName = (name: string, category?: string): string => {
    if (!name) return 'N/A';
    
    // If category is mowing, normalize to standard name
    if (category === 'mowing') {
      const normalized = name.trim().toLowerCase();
      // Check if this is a lawn mowing/edging/trimming service in any variation
      if (normalized.includes('mowing') || 
          normalized.includes('trim') || 
          normalized.includes('edge') ||
          normalized.includes('edging')) {
        return 'Lawn Mowing/Trimming';
      }
    }
    
    // Return original name for other service types
    return name;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Service Search & Records
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Search completed services for legal, tax, and customer service purposes. All data linked via ServiceRequest ID (single source of truth).
        </Typography>

        {/* Search Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Customer First Name"
                value={filters.customerFirstName}
                onChange={(e) => handleFilterChange('customerFirstName', e.target.value)}
                placeholder="First name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Customer Last Name"
                value={filters.customerLastName}
                onChange={(e) => handleFilterChange('customerLastName', e.target.value)}
                placeholder="Last name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Provider Name"
                value={filters.providerName}
                onChange={(e) => handleFilterChange('providerName', e.target.value)}
                placeholder="First name, last name, or business name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Provider Email"
                value={filters.providerEmail}
                onChange={(e) => handleFilterChange('providerEmail', e.target.value)}
                placeholder="provider@example.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Service Type</InputLabel>
                <Select
                  value={filters.serviceType}
                  onChange={(e) => handleFilterChange('serviceType', e.target.value)}
                  label="Service Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="mowing">Lawn Mowing</MenuItem>
                  <MenuItem value="fertilizing">Fertilizing</MenuItem>
                  <MenuItem value="snow_removal">Snow Removal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={filters.completedStartDate}
                onChange={(e) => handleFilterChange('completedStartDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={filters.completedEndDate}
                onChange={(e) => handleFilterChange('completedEndDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={() => handleSearch(1)}
                disabled={loading}
                sx={{ mr: 2 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({
                    customerFirstName: '',
                    customerLastName: '',
                    providerName: '',
                    providerEmail: '',
                    serviceType: '',
                    completedStartDate: '',
                    completedEndDate: '',
                  });
                  setServices([]);
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Results */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : services.length > 0 ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Found {pagination.total} service(s) - Page {pagination.page} of {pagination.pages}
              </Typography>
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50px"></TableCell>
                    <TableCell>Service Request ID</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Service Type</TableCell>
                    <TableCell>Completed Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Stripe Confirmation</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {services.map((service) => {
                    const isExpanded = expandedRows.has(service.serviceRequest._id);
                    return (
                      <React.Fragment key={service.serviceRequest._id}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpansion(service.serviceRequest._id)}
                            >
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {service.serviceRequest._id.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {service.serviceRequest.customer ? (
                              <>
                                {service.serviceRequest.customer.firstName} {service.serviceRequest.customer.lastName}
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  {service.serviceRequest.customer.email}
                                </Typography>
                              </>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell>
                            {service.serviceRequest.provider ? (
                              <>
                                {service.serviceRequest.provider.businessName || 
                                 `${service.serviceRequest.provider.firstName} ${service.serviceRequest.provider.lastName}`}
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  {service.serviceRequest.provider.email}
                                </Typography>
                              </>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={formatServiceTypeName(service.serviceRequest.serviceType.name, service.serviceRequest.serviceType.category)}
                              size="small"
                              color={
                                service.serviceRequest.serviceType.category === 'mowing' ? 'primary' :
                                service.serviceRequest.serviceType.category === 'fertilizing' ? 'success' :
                                'warning'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(service.serviceRequest.completedDate)}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(service.payment?.amount)}
                          </TableCell>
                          <TableCell>
                            {service.payment?.stripePaymentIntentId ? (
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                {service.payment.stripePaymentIntentId}
                              </Typography>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setDetailDialog({ open: true, service })}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="subtitle2" gutterBottom>
                                          <Person /> Customer Information
                                        </Typography>
                                        {service.serviceRequest.customer && (
                                          <>
                                            <Typography variant="body2">
                                              <strong>Name:</strong> {service.serviceRequest.customer.firstName} {service.serviceRequest.customer.lastName}
                                            </Typography>
                                            <Typography variant="body2">
                                              <strong>Email:</strong> {service.serviceRequest.customer.email}
                                            </Typography>
                                            {service.serviceRequest.customer.phone && (
                                              <Typography variant="body2">
                                                <strong>Phone:</strong> {service.serviceRequest.customer.phone}
                                              </Typography>
                                            )}
                                          </>
                                        )}
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="subtitle2" gutterBottom>
                                          <Business /> Provider Information
                                        </Typography>
                                        {service.serviceRequest.provider && (
                                          <>
                                            <Typography variant="body2">
                                              <strong>Name:</strong> {service.serviceRequest.provider.businessName || 
                                                `${service.serviceRequest.provider.firstName} ${service.serviceRequest.provider.lastName}`}
                                            </Typography>
                                            <Typography variant="body2">
                                              <strong>Email:</strong> {service.serviceRequest.provider.email}
                                            </Typography>
                                          </>
                                        )}
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="subtitle2" gutterBottom>
                                          <Payment /> Payment Information
                                        </Typography>
                                        {service.payment ? (
                                          <>
                                            <Typography variant="body2">
                                              <strong>Amount:</strong> {formatCurrency(service.payment.amount)}
                                            </Typography>
                                            <Typography variant="body2">
                                              <strong>Provider Cut:</strong> {formatCurrency(service.payment.providerCut)}
                                            </Typography>
                                            <Typography variant="body2">
                                              <strong>Platform Cut:</strong> {formatCurrency(service.payment.platformCut)}
                                            </Typography>
                                            {service.payment.stripePaymentIntentId && (
                                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mt: 1 }}>
                                                <strong>Payment Intent:</strong> {service.payment.stripePaymentIntentId}
                                              </Typography>
                                            )}
                                            {service.payment.stripeChargeId && (
                                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                <strong>Charge ID:</strong> {service.payment.stripeChargeId}
                                              </Typography>
                                            )}
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                              <strong>Status:</strong> <Chip label={service.payment.status} size="small" />
                                            </Typography>
                                            {service.payment.stripeSettlementStatus && (
                                              <Typography variant="body2" sx={{ mt: 1 }}>
                                                <strong>Settlement:</strong> <Chip label={service.payment.stripeSettlementStatus} size="small" />
                                              </Typography>
                                            )}
                                          </>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            No payment record found
                                          </Typography>
                                        )}
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="subtitle2" gutterBottom>
                                          <LocationOn /> Service Location
                                        </Typography>
                                        <Typography variant="body2">
                                          {service.serviceRequest.address.street}
                                        </Typography>
                                        <Typography variant="body2">
                                          {service.serviceRequest.address.city}, {service.serviceRequest.address.state} {service.serviceRequest.address.zipCode}
                                        </Typography>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                  {service.jobCompletion && (
                                    <>
                                      <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                          <CardContent>
                                            <Typography variant="subtitle2" gutterBottom>
                                              <Notes /> Completion Notes
                                            </Typography>
                                            {service.jobCompletion.notes ? (
                                              <Typography variant="body2">{service.jobCompletion.notes}</Typography>
                                            ) : (
                                              <Typography variant="body2" color="text.secondary">No notes</Typography>
                                            )}
                                            {service.jobCompletion.issues && service.jobCompletion.issues.length > 0 && (
                                              <Box sx={{ mt: 1 }}>
                                                <Typography variant="caption" color="error">
                                                  <strong>Issues:</strong> {service.jobCompletion.issues.join(', ')}
                                                </Typography>
                                              </Box>
                                            )}
                                          </CardContent>
                                        </Card>
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                          <CardContent>
                                            <Typography variant="subtitle2" gutterBottom>
                                              <PhotoCamera /> Photos
                                            </Typography>
                                            <Typography variant="body2">
                                              Before: {service.jobCompletion.beforePhotos?.length || 0}
                                            </Typography>
                                            <Typography variant="body2">
                                              After: {service.jobCompletion.afterPhotos?.length || 0}
                                            </Typography>
                                          </CardContent>
                                        </Card>
                                      </Grid>
                                    </>
                                  )}
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
                <Button
                  disabled={pagination.page === 1}
                  onClick={() => handleSearch(pagination.page - 1)}
                >
                  Previous
                </Button>
                <Typography variant="body2" sx={{ alignSelf: 'center', mx: 2 }}>
                  Page {pagination.page} of {pagination.pages}
                </Typography>
                <Button
                  disabled={pagination.page === pagination.pages}
                  onClick={() => handleSearch(pagination.page + 1)}
                >
                  Next
                </Button>
              </Box>
            )}
          </>
        ) : services.length === 0 && !loading ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No services found. Try adjusting your search filters.
            </Typography>
          </Paper>
        ) : null}
      </Box>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, service: null })}
        maxWidth="md"
        fullWidth
      >
        {detailDialog.service && (
          <>
            <DialogTitle>
              Service Details - {detailDialog.service.serviceRequest.serviceRequestId.substring(0, 8)}...
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Service Request (Primary Record)
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  ID: {detailDialog.service.serviceRequest._id}
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                {detailDialog.service.payment && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Payment Information (Legal/Tax)
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                      Stripe Payment Intent: {detailDialog.service.payment.stripePaymentIntentId || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                      Stripe Charge ID: {detailDialog.service.payment.stripeChargeId || 'N/A'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                  </>
                )}

                {detailDialog.service.jobCompletion && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Completion Details
                    </Typography>
                    <Typography variant="body2">
                      Check-in: {formatDate(detailDialog.service.jobCompletion.checkInTime)}
                    </Typography>
                    <Typography variant="body2">
                      Check-out: {formatDate(detailDialog.service.jobCompletion.checkOutTime)}
                    </Typography>
                    <Typography variant="body2">
                      Completed: {formatDate(detailDialog.service.jobCompletion.completedAt)}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialog({ open: false, service: null })}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default AdminServiceSearchPage;

